import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link2, ArrowDownLeft, ArrowUpRight, ExternalLink, RefreshCw, Search, Repeat } from "lucide-react";
import { Gate } from "@/components/wallet/Gate";
import { useWallet } from "@/lib/wallet/store";
import { activityQuery } from "@/lib/wallet/queries";
import { addressFingerprint, formatAmount, formatLedgerTime, truncateAddress } from "@/lib/wallet/format";
import type { LedgerActivity } from "@/lib/wallet/xrpl";

export const Route = createFileRoute("/activity")({
  staticData: { sitemap: false },
  head: () => ({
    meta: [
      { title: "Transaction history — Cobalt Wallet" },
      {
        name: "description",
        content:
          "Search and filter your full XRPL transaction history: payments in and out, swaps, trustlines and NFT activity.",
      },
      { property: "og:title", content: "Transaction history — Cobalt Wallet" },
      {
        property: "og:description",
        content: "Filter XRPL activity by direction, asset, result and counterparty.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: () => (
    <Gate>
      <ActivityScreen />
    </Gate>
  ),
});

type DirectionFilter = "all" | "in" | "out" | "other";
type ResultFilter = "all" | "ok" | "failed";

const DIRECTIONS: { label: string; value: DirectionFilter }[] = [
  { label: "All", value: "all" },
  { label: "Received", value: "in" },
  { label: "Sent", value: "out" },
  { label: "Other", value: "other" },
];

function ActivityScreen() {
  const { address, contacts } = useWallet();
  const query = useQuery(activityQuery(address));
  const [term, setTerm] = useState("");
  const [direction, setDirection] = useState<DirectionFilter>("all");
  const [result, setResult] = useState<ResultFilter>("all");
  const [asset, setAsset] = useState("all");

  const rows = query.data ?? [];

  const assets = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => r.code && set.add(r.code));
    return ["all", ...Array.from(set).sort()];
  }, [rows]);

  const labelFor = (addr: string) =>
    contacts.find((c) => c.address === addr)?.label ?? null;

  const filtered = useMemo(() => {
    const q = term.trim().toLowerCase();
    return rows.filter((row) => {
      if (direction === "in" && row.direction !== "in") return false;
      if (direction === "out" && row.direction !== "out") return false;
      if (direction === "other" && row.kind === "Payment") return false;
      if (result === "ok" && !row.succeeded) return false;
      if (result === "failed" && row.succeeded) return false;
      if (asset !== "all" && row.code !== asset) return false;
      if (!q) return true;
      const label = labelFor(row.counterparty) ?? "";
      return (
        row.hash.toLowerCase().includes(q) ||
        row.counterparty.toLowerCase().includes(q) ||
        row.kind.toLowerCase().includes(q) ||
        row.amount.toLowerCase().includes(q) ||
        label.toLowerCase().includes(q)
      );
    });
  }, [rows, term, direction, result, asset, contacts]);

  const totals = useMemo(() => {
    let inXrp = 0;
    let outXrp = 0;
    filtered.forEach((r) => {
      if (r.code !== "XRP" || !r.succeeded) return;
      if (r.direction === "in") inXrp += Number(r.amount) || 0;
      if (r.direction === "out") outXrp += Number(r.amount) || 0;
    });
    return { inXrp, outXrp };
  }, [filtered]);

  return (
    <div className="animate-rise space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-[18px] font-medium tracking-tight">Activity</h1>
          <p className="readout mt-1 text-[10px] text-faint">
            Read straight from the XRPL — nothing is indexed by us.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void query.refetch()}
          className="btn-quiet grid size-9 place-items-center"
          aria-label="Refresh history"
        >
          <RefreshCw className={`size-3.5 ${query.isFetching ? "animate-spin" : ""}`} />
        </button>
      </div>

      <section className="panel space-y-3 p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-faint" />
          <input
            value={term}
            onChange={(e) => setTerm(e.target.value.slice(0, 80))}
            placeholder="Search hash, address, label, amount"
            className="field h-10 w-full pl-9 pr-3 text-[12px]"
          />
        </div>

        <div className="grid grid-cols-4 gap-1.5">
          {DIRECTIONS.map((d) => (
            <button
              key={d.value}
              type="button"
              onClick={() => setDirection(d.value)}
              className={`readout h-8 rounded-lg border text-[10px] uppercase tracking-[0.1em] ${
                direction === d.value ? "chip-active" : "border-border text-muted"
              }`}
            >
              {d.label}
            </button>
          ))}
        </div>

        <div className="flex gap-1.5">
          <select
            value={asset}
            onChange={(e) => setAsset(e.target.value)}
            className="field h-8 flex-1 px-2 text-[10px] uppercase tracking-[0.1em]"
          >
            {assets.map((a) => (
              <option key={a} value={a}>
                {a === "all" ? "All assets" : a}
              </option>
            ))}
          </select>
          <select
            value={result}
            onChange={(e) => setResult(e.target.value as ResultFilter)}
            className="field h-8 flex-1 px-2 text-[10px] uppercase tracking-[0.1em]"
          >
            <option value="all">Any result</option>
            <option value="ok">Succeeded</option>
            <option value="failed">Failed</option>
          </select>
        </div>

        <div className="readout flex items-center justify-between text-[10px] text-faint">
          <span>{filtered.length} of {rows.length} entries</span>
          <span>
            <span className="text-success">+{formatAmount(totals.inXrp)}</span>{" "}
            <span className="text-danger">−{formatAmount(totals.outXrp)}</span> XRP
          </span>
        </div>
      </section>

      {query.isLoading ? (
        <div className="panel grid h-32 place-items-center">
          <p className="label-instrument">Reading the ledger…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="panel grid h-32 place-items-center px-6 text-center">
          <p className="readout text-[11px] leading-relaxed text-faint">
            {rows.length === 0
              ? "No ledger activity for this account yet."
              : "No entries match these filters."}
          </p>
        </div>
      ) : (
        <ul className="panel divide-y divide-border">
          {filtered.map((row) => (
            <ActivityRow key={row.hash} row={row} label={labelFor(row.counterparty)} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ActivityRow({ row, label }: { row: LedgerActivity; label: string | null }) {
  const [open, setOpen] = useState(false);
  const isPayment = row.kind === "Payment";
  const isTrustline = row.kind === "TrustSet";
  const Icon = isTrustline ? Link2 : !isPayment ? Repeat : row.direction === "in" ? ArrowDownLeft : ArrowUpRight;

  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-3 p-3 text-left"
      >
        <span
          className={`grid size-8 shrink-0 place-items-center rounded-lg ${
            !row.succeeded
              ? "bg-danger/10 text-danger"
              : isTrustline
                ? "border border-accent/40 bg-accent/12 text-accent"
                : row.direction === "in"
                ? "bg-success/10 text-success"
                : "accent-tint"
          }`}
        >
          <Icon className="size-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px]">
            {label ??
              (isPayment
                ? row.direction === "in"
                  ? "Received"
                  : "Sent"
                : isTrustline
                  ? `Trustline set${row.code ? ` · ${row.code}` : ""}`
                  : row.kind)}
          </span>
          <span className="readout mt-0.5 block truncate text-[10px] text-faint">
            {formatLedgerTime(row.date)} · {truncateAddress(row.counterparty, 4, 4)}
          </span>
        </span>
        <span className="readout shrink-0 text-right text-[12px]">
          <span className={row.direction === "in" ? "text-success" : ""}>
            {row.direction === "in" ? "+" : row.direction === "out" ? "−" : ""}
            {formatAmount(row.amount)}
          </span>
          <span className="mt-0.5 block text-[9px] text-faint">{row.code || row.kind}</span>
        </span>
      </button>

      {open ? (
        <div className="animate-tick space-y-2 border-t border-border bg-background/40 p-3">
          <dl className="readout space-y-1.5 text-[10px]">
            <Row k="TYPE" v={row.kind} />
            <Row k="RESULT" v={row.succeeded ? "tesSUCCESS" : "failed"} />
            <Row k="COUNTERPARTY" v={truncateAddress(row.counterparty, 8, 8)} />
            <Row k="FINGERPRINT" v={addressFingerprint(row.counterparty)} />
            <Row k="HASH" v={truncateAddress(row.hash, 8, 8)} />
          </dl>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void navigator.clipboard.writeText(row.hash)}
              className="btn-quiet h-8 flex-1"
            >
              Copy hash
            </button>
            <a
              href={`https://livenet.xrpl.org/transactions/${row.hash}`}
              target="_blank"
              rel="noreferrer noopener"
              className="btn-outline-accent flex h-8 flex-1 items-center justify-center gap-1.5"
            >
              Explorer <ExternalLink className="size-3" />
            </a>
          </div>
        </div>
      ) : null}
    </li>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-faint">{k}</dt>
      <dd className="text-right">{v}</dd>
    </div>
  );
}
